import React, { useEffect } from 'react';
import { fabric } from 'fabric';
import { Canvas, StaticCanvas } from 'fabric/fabric-impl';
import { Coords } from 'types/evrima';
import { resolve } from 'path';
import { createUseStyles } from 'react-jss';

fabric.textureSize = 4096;

const useStyles = createUseStyles((theme) => ({
  canv: {
    width: '100%'
  },
  cont: {
    display: 'flex',
    width: '100%'
  }
}));

type Position = {
  x: number;
  y: number;
  scale: number;
  rotation: number;
};

export type FilterOptions = 'bw' | 'om' | 'im' | 'invert' | 'high-power' | 'cc';

type Props = {
  backgroundURL?: string;
  threatURL?: string;
  quality?: number;
  position?: Position;
  hidden?: boolean;
  width?: number;
  zoomStatus?: number; // Number between 1 and 9 representing the section to zoom in on
  height: number;
  showThreat?: boolean;
  className?: string;
  filter?: FilterOptions[];
  onClick?: (
    isThreat: boolean,
    mousePosition: Coords,
    imagePosition: Coords
  ) => void;
  onLoad?: (dimensions: { width: number; height: number }) => void; // Called when the simulator image has loaded and can be seen.
  imageWidth?: number;
};

const SimulatorImage = ({
  backgroundURL,
  threatURL,
  hidden,
  quality = 50,
  zoomStatus,
  filter,
  position,
  width,
  showThreat,
  height,
  onClick,
  onLoad,
  className
}: Props) => {
  const classes = useStyles();
  // random string generator
  const randomString = () => {
    return Math.random().toString(36).substring(7);
  };

  const [canvasName] = React.useState(randomString());

  const canvasRef = React.useRef<StaticCanvas | Canvas | null>(null);

  const containerRef = React.useRef<HTMLDivElement | null>(null);

  const cache = React.useRef<{ [url: string]: fabric.Image }>({});

  const loaded = React.useRef<boolean>(false);
  const imageLoading = React.useRef<boolean>(false);

  const fetchImage = (
    url?: string,
    params?: { [key: string]: string | number }
  ): Promise<fabric.Image | undefined> => {
    if (!url) return new Promise((resolve) => resolve(undefined));

    let transformURL = url + `?quality=${quality}`;
    for (const key in params) {
      if (params[key]) {
        transformURL += `&${key}=${params[key]}`;
      }
    }

    transformURL += `&width=2000`;

    if (cache?.current?.[transformURL]) {
      return new Promise((resolve) => {
        // clone cache image to avoid modifying the original
        resolve(cache.current?.[transformURL]);
      });
    }

    return new Promise((resolve, reject) => {
      fabric.Image.fromURL(
        transformURL,
        (img) => {
          if (cache.current) {
            cache.current[transformURL] = img;
          }

          resolve(img);
        },
        { crossOrigin: 'anonymous' }
      );
    });
  };

  const addTouchFeedback = (canvas: StaticCanvas | Canvas) => {
    // Needs to be run after canvas height is set.
    const touchFeedback = new fabric.Rect({
      width: canvas.getWidth(),
      height: canvas.getHeight(),
      top: 0,
      left: 0,
      fill: 'rgba(0,0,0,0)',
      hoverCursor: 'default',
      selectable: false
    });
    touchFeedback.on('mousedown', onFeedbackClick);

    canvas.add(touchFeedback);

    touchFeedback.moveTo(3);
    return touchFeedback;
  };

  const addThreat = async (
    canvas: StaticCanvas | Canvas,
    threat: fabric.Image,
    position: Position,
    width: number,
    offset?: { top: number | undefined; left: number | undefined }
  ) => {
    threat = await addFilter(threat, true);

    threat.set({
      angle: 0,
      top: 0,
      left: 0,
      originX: 'center',
      originY: 'center'
    });

    threat.setCoords();
    threat.scaleToWidth(width, true);

    const scaleFactor = threat.getObjectScaling().scaleX;

    // Scaling is based on the size of the threat image.
    const scale = scaleFactor * position.scale;

    threat.set({
      left: position.x * scaleFactor + (offset?.left ?? 0),
      top: position.y * scaleFactor + (offset?.top ?? 0),
      scaleX: scale,
      scaleY: scale,
      angle: position.rotation || 0,
      globalCompositeOperation: filter?.includes('invert')
        ? 'lighten'
        : 'darken',
      selectable: false,
      hoverCursor: 'default'
    });

    threat.on('mousedown', onThreatClick);
    threat.moveTo(0);

    canvas.add(threat);
  };

  const fetchCC = async (
    url: string,
    params?: {
      [key: string]: string | number;
    }
  ) => {
    const newImage = await fetchImage(url, {
      sharpen: 5,
      gamma: 2,
      saturation: 1.3,
      claheWidth: 20,
      claheHeight: 20,
      quality: 50,
      ...params
    });

    return newImage;
  };

  const addBackground = async (
    canvas: fabric.Canvas | StaticCanvas,
    background: fabric.Image,
    width: number,
    height: number,
    showThreat?: boolean
  ) => {
    background = await addFilter(background, false);

    background.set({ left: 0, top: 0, selectable: false, opacity: 1 });
    background.moveTo(0);

    const ratioImage = (background.width ?? 0) / (background.height ?? 0);
    const ratioCanvas = width / height;

    if (ratioCanvas > ratioImage) {
      background.scaleToHeight(height, true);
    } else {
      background.scaleToWidth(width, true);
    }

    if (showThreat) {
      background.set({ opacity: 0.3 });
    }
    canvas.centerObject(background);

    canvas.add(background);
  };

  const addFilter = async (img: fabric.Image, threat: boolean) => {
    if (filter?.includes('cc')) {
      // Make unsharp mask
      if (!containerRef.current) return img;

      if (threat && threatURL) {
        const newImage = await fetchCC(threatURL);
        if (!newImage) return img;
        img = newImage;
      }

      if (!threat && backgroundURL) {
        const newImage = await fetchCC(backgroundURL);
        if (!newImage) return img;
        img = newImage;
      }
    }

    img.filters = [];

    if (filter?.includes('bw')) {
      const filter = new fabric.Image.filters.Grayscale();
      img.filters?.push(filter);
    }
    if (filter?.includes('im')) {
      const filter = new fabric.Image.filters.ColorMatrix({
        matrix: [1, 1, 0, 0, 0, 1, 1, 0, 0, 0, 1, 1, 2, 0, 0, 0, 0, 0, 1, 0]
      });
      img.filters?.push(filter);
    }
    if (filter?.includes('om')) {
      const filter = new fabric.Image.filters.ColorMatrix({
        matrix: [1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 0, 1, 0.5, -2, 1, 0]
      });

      img.filters?.push(filter);
    }
    if (filter?.includes('invert')) {
      const filter = new fabric.Image.filters.ColorMatrix({
        matrix: [
          0.587,
          -1.38,
          -0.208,
          1.0,
          0.0,
          -0.435,
          -0.44,
          -0.124,
          1.0,
          0.0,
          -0.371,
          -1.48,
          0.849,
          1.0,
          0.0,
          0.0,
          0.0,
          0.0,
          1.0,
          0.0
        ]
      });
      img.filters?.push(filter);
    }
    if (filter?.includes('high-power')) {
      // @ts-ignore
      const filter = new fabric.Image.filters.Gamma({
        gamma: [2, 2, 2]
      });
      img.filters?.push(filter);
      const filter1 = new fabric.Image.filters.Brightness({
        brightness: 0.1
      });
      img.filters?.push(filter1);
      const filter2 = new fabric.Image.filters.Saturation({
        saturation: 0.6
      });
      img.filters?.push(filter2);
    }

    if (
      filter &&
      filter.length > 0 &&
      !filter.includes('high-power') &&
      !filter.includes('om') &&
      !filter.includes('cc')
    ) {
      const filter = new fabric.Image.filters.Contrast({
        contrast: 0.1
      });
      img.filters?.push(filter);
    }

    img.applyFilters();
    return img;
  };

  const onCanvasLoaded = () => {
    if (loaded.current) return;
    loaded.current = true;
  };

  const onFeedbackClick = (e: fabric.IEvent<MouseEvent>) => {
    onClick &&
      onClick(
        false,
        { x: e.e.pageX, y: e.e.pageY },
        { x: e.pointer?.x ?? 0, y: e.pointer?.y ?? 0 }
      );
  };

  const onThreatClick = (e: fabric.IEvent<MouseEvent>) => {
    onClick &&
      onClick(
        true,
        { x: e.e.pageX, y: e.e.pageY },
        { x: e.pointer?.x ?? 0, y: e.pointer?.y ?? 0 }
      );
  };

  const updateCanvas = async (
    backgroundURL: {
      url: string;
    },
    threatURL: string | undefined,
    dims: {
      width: number;
      height: number;
    },
    position?: Position,
    showThreatItem?: boolean
  ) => {
    if (!canvasRef?.current) {
      const config = {
        renderOnAddRemove: false,
        preserveObjectStacking: true,
        selectable: false
      };
      if (onClick) {
        canvasRef.current = new fabric.Canvas(canvasName, config);
      } else {
        // When we don't need to handle clicks, use the more performant StaticCanvas instead
        canvasRef.current = new fabric.StaticCanvas(canvasName, config);
      }
    }

    const canvas = canvasRef.current;

    const getCanvasDims = () => {
      return { width: dims.width, height: dims.height ?? 0 };
    };

    const d = getCanvasDims();
    canvas.setDimensions(d);

    // Fixes issue where blank screen on first load
    if (d.width == 0) {
      return;
    }

    if (loaded.current) {
      // If canvas is already loaded, just run scaling again
    }

    // Only allow one loading to happen at a time.
    // prevents races between images as this runs async
    if (imageLoading.current) {
      return;
    } else {
      imageLoading.current = true;
    }
    const [background, threat] = await Promise.all([
      fetchImage(backgroundURL.url),
      fetchImage(threatURL)
    ]);

    imageLoading.current = false;

    // Preload CC images
    fetchCC(backgroundURL.url);
    if (threatURL) fetchCC(threatURL);

    canvas.clear();

    if (filter?.includes('invert')) {
      canvas.backgroundColor = '#000000';
    } else {
      canvas.backgroundColor = '#ffffff';
    }

    // Run Background placement
    if (!background) return;

    await addBackground(
      canvas,
      background,
      dims.width,
      dims.height,
      showThreatItem
    );

    if (!threat || !position) {
      addTouchFeedback(canvas);
      canvas.renderAll();
      onCanvasLoaded();
      zoomViewport(zoomStatus);
      onLoad && onLoad(d);
      return;
    }

    addTouchFeedback(canvas);

    // Run threat placement
    await addThreat(canvas, threat, position, background.getScaledWidth(), {
      left: background.left,
      top: background.top
    });

    canvas.requestRenderAll();
    onCanvasLoaded();
    zoomViewport(zoomStatus);
    onLoad && onLoad(d);
  };

  const zoomViewport = (zoomStatus?: number) => {
    if (!canvasRef.current) return;

    const trans = canvasRef.current.viewportTransform;
    if (!trans) return;

    let scale = 1;
    let translationX = 0;
    let translationY = 0;

    if (zoomStatus) {
      scale = 1.5;
    }

    // Left buttons
    if (zoomStatus == 4 || zoomStatus == 7 || zoomStatus == 1) {
      translationX = 0;
    }

    // Middle Buttons
    if (zoomStatus == 5 || zoomStatus == 8 || zoomStatus == 2) {
      translationX = -canvasRef.current.getWidth() * 0.25;
    }

    // Right buttons
    if (zoomStatus == 9 || zoomStatus == 6 || zoomStatus == 3) {
      translationX = -canvasRef.current.getWidth() * 0.5;
    }

    // Top buttons
    if (zoomStatus == 7 || zoomStatus == 8 || zoomStatus == 9) {
      translationY = 0;
    }

    // Middle buttons
    if (zoomStatus == 4 || zoomStatus == 5 || zoomStatus == 6) {
      translationY = -canvasRef.current.getHeight() * 0.25;
    }

    // Bottom buttons
    if (zoomStatus == 1 || zoomStatus == 2 || zoomStatus == 3) {
      translationY = -canvasRef.current.getHeight() * 0.5;
    }

    // canvasRef.current.setZoom(1);
    // Zoom X & Y
    trans[0] = scale;
    trans[3] = scale;

    // Translation X & Y
    trans[4] = translationX;
    trans[5] = translationY;

    canvasRef.current.setViewportTransform(trans);
    canvasRef.current.requestRenderAll();
  };

  React.useLayoutEffect(() => {
    if (containerRef.current) {
      if (!backgroundURL) {
        return;
      }
      if (!containerRef.current) return;

      updateCanvas(
        {
          url: backgroundURL
        },
        threatURL,
        {
          width: width ? width : containerRef.current.clientWidth,
          height: height
        },
        position,
        showThreat
      );
    }
  }, [
    containerRef.current?.clientWidth,
    zoomStatus,
    filter,
    backgroundURL,
    threatURL,
    position,
    showThreat,
    width,
    height
  ]);

  // Dispose of current canvas when unmounting
  React.useEffect(() => {
    return () => {
      if (canvasRef.current) {
        canvasRef.current.dispose();
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={`${classes.cont} ${className}`}
      style={{ opacity: hidden ? 0 : 1 }}
    >
      <canvas id={canvasName} className={classes.canv} />
    </div>
  );
};

export default SimulatorImage;
